import React, { useEffect, useState } from "react";
import type {
  Fare,
  FareSliceOutbound,
  Flights,
} from "@b2bportal/air-disruption-api";
import {
  DisruptionExerciseButton,
  type FetchTripDetailsThunk,
  useDisruptionFlightCard,
} from "@b2bportal/core-disruption";
import { DefaultFlightSlotProvider } from "@b2bportal/core-flights";
import {
  IconNameEnum,
  useDisruptionStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreDisruptionComponent,
  type FlightFareSelectionFunction,
  type IFlightListData,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { FlightCardSummary, NearbyAirportLabel } from "@tenant/disruption";
import {
  FlightCardDetailsLoading,
  FlightCardFareList,
  FlightCardWrapper,
  FlightDetails,
  FlightDetailsDialog,
  FlightFareGrid,
} from "@tenant/flights";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import { useNearbyAirports } from "../DisruptionExerciseShopFilter/NearbyAirportsContext";
import defaultStyles from "./DisruptionExerciseFlightCard.module.scss";

export interface DisruptionExerciseFlightCardProps {
  flight: IFlightListData<Fare | FareSliceOutbound>;
  isSelected: boolean;
  sliceIndex: number;
  onClick?: () => void;
  onFareSelect: FlightFareSelectionFunction;
  goToNextStep: () => void;
  className?: string;
  flights: Flights;
  fetchTripDetails: FetchTripDetailsThunk;
}

export const DisruptionExerciseFlightCard = React.forwardRef<
  HTMLDivElement,
  DisruptionExerciseFlightCardProps
>((props, forwardedRef) => {
  const {
    flight,
    isSelected,
    sliceIndex = 0,
    onFareSelect,
    goToNextStep,
    className,
  } = props;

  const COMPONENT_KEY = CoreDisruptionComponent.DisruptionExerciseFlightCard;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { isDesktopAndUp } = useDeviceType();
  const { t } = useI18nContext();

  const {
    context: {
      isFlightDetailsLoading,
      open,
      flightCardSummary,
      fareData,
      tripCategory,
      fareShelfFilter,
      flightDetails,
      flightSlice,
      fareShelfFilterWithUpsell,
      openFlightDetailsDialog,
    },
    handlers: {
      handleFlightCardClick,
      setOpenFlightDetailsDialog,
      handleFareGridSelection,
    },
  } = useDisruptionFlightCard(props);

  const nearbyCtx = useNearbyAirports();
  const isNearbyAirport =
    flightSlice != null &&
    nearbyCtx.isNearbyAirport(flightSlice.destinationCode);

  const [pendingDialog, setPendingDialog] = useState(false);

  useEffect(() => {
    if (
      pendingDialog &&
      !isFlightDetailsLoading &&
      flightDetails != null &&
      !isDesktopAndUp
    ) {
      setOpenFlightDetailsDialog(true);
      setPendingDialog(false);
    }
  }, [
    pendingDialog,
    isFlightDetailsLoading,
    flightDetails,
    setOpenFlightDetailsDialog,
    isDesktopAndUp,
  ]);

  return (
    <DefaultFlightSlotProvider>
      <div className={clsx(block, className)} ref={forwardedRef}>
        <FlightCardWrapper
          className={cn("button")}
          onClick={() => {
            handleFlightCardClick();
            if (!openFlightDetailsDialog) {
              setPendingDialog(true);
            }
          }}
        >
          {flightCardSummary == null ? null : (
            <div className={cn("summary")}>
              <FlightCardSummary {...flightCardSummary} />
            </div>
          )}
          <div className={cn("fare-grid")}>
            <FlightFareGrid
              fares={fareData}
              tripType={tripCategory}
              onFareGridClick={handleFareGridSelection}
              fareShelfFilter={fareShelfFilter}
            />
          </div>
          <Icon
            iconName={IconNameEnum.dropdownTriangle}
            className={clsx(
              cn("icon", {
                expanded: isSelected,
              })
            )}
          />
        </FlightCardWrapper>

        {open && (
          <>
            <div
              className={cn("flight-card-details", {
                open: open,
                loading: isFlightDetailsLoading,
              })}
            >
              {isFlightDetailsLoading ||
              flightDetails == null ||
              flightSlice == null ? (
                <FlightCardDetailsLoading />
              ) : (
                <>
                  <div className={cn("flight-details")}>
                    <FlightDetails
                      flightDetails={flightDetails}
                      flightSlice={flightSlice}
                      planeInfo=""
                      isOutgoing={sliceIndex === 0}
                      classNames=""
                    >
                      {isNearbyAirport && (
                        <NearbyAirportLabel
                          className={cn("nearby-airport-banner")}
                        >
                          <Trans
                            i18nKey="core-disruption.nearbyAirportBanner"
                            values={{
                              nearbyAirport: flightSlice.destinationCode,
                              originalAirport: nearbyCtx.destinationAirportCode,
                            }}
                            tOptions={{ interpolation: { escapeValue: false } }}
                          />
                        </NearbyAirportLabel>
                      )}
                    </FlightDetails>
                  </div>
                  <div className={cn("fare-details")}>
                    <FlightCardFareList
                      trip={flightDetails}
                      fareIdList={flight.fares.map((f) => {
                        return "example" in f ? f.fareSlice : f.id;
                      })}
                      fareDetails={flightDetails.fareDetails}
                      sliceIndex={sliceIndex}
                      fareShelfFilter={fareShelfFilterWithUpsell}
                      sliceId={flight.sliceId}
                      tripId={flightDetails.id}
                      onFareSelect={onFareSelect}
                      goToNextStep={
                        !isDesktopAndUp
                          ? () => setOpenFlightDetailsDialog(true)
                          : goToNextStep
                      }
                    />
                  </div>
                </>
              )}
            </div>
            {isDesktopAndUp &&
              !isFlightDetailsLoading &&
              flightDetails != null && (
                <div className={cn("select-flight-button-root")}>
                  <DisruptionExerciseButton
                    title={t(
                      "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.selectFlight"
                    )}
                    onClick={goToNextStep}
                    disabled={false}
                  />
                </div>
              )}
          </>
        )}
        <FlightDetailsDialog
          open={openFlightDetailsDialog}
          onClose={() => {
            setOpenFlightDetailsDialog(false);
            setPendingDialog(false);
            props.onClick?.();
          }}
          onClick={() => {
            goToNextStep();
          }}
          flightSlice={flightSlice}
          isOutbound={sliceIndex === 0}
          fareSlice={flightDetails?.fareDetails[0]?.slices[sliceIndex]}
          segments={flightSlice?.segmentDetails}
        />
      </div>
    </DefaultFlightSlotProvider>
  );
});
